import React, { useCallback, useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { listTutors } from "../lib/api";
import { Avatar } from "../components/Avatar";

const ACCENTS = [
  { id: null, label: "Todos" },
  { id: "UK", label: "Reino Unido" },
  { id: "US", label: "EE. UU." },
  { id: "CA", label: "Canadá" },
  { id: "AU", label: "Australia" },
  { id: "IE", label: "Irlanda" },
];

const SPECIALTIES = ["Preparación IELTS", "Conversación fluida", "Business English", "Pronunciación", "Conversación", "TOEFL", "Tech English", "Conversación relajada", "Entrevistas de trabajo", "Small talk"];

const SORTS = [
  { id: "popular", label: "Populares" },
  { id: "price_asc", label: "Disponibilidad ↑" },
  { id: "price_desc", label: "Disponibilidad ↓" },
  { id: "rating", label: "Mejor valorados" },
  { id: "recent", label: "Recién llegados" },
];

const TutorCard = ({ t }) => (
  <Link
    to={`/tutor/${t.id}`}
    className="wk-card wk-card-hover p-6 flex flex-col gap-4 transition-all"
    data-testid={`tutor-card-${t.id}`}
  >
    <div className="flex items-start gap-4">
      <Avatar initials={t.initials} color={t.avatar_color} size={56} />
      <div className="flex-1 min-w-0">
        <div className="flex items-center gap-2 flex-wrap">
          <div className="font-semibold text-[17px]" style={{ color: "#1E1924" }}>{t.display_name}</div>
          <span className="text-[10px] px-1.5 py-0.5 rounded font-medium tracking-wide uppercase" style={{ background: "rgba(30,25,36,0.06)", color: "#70667A" }}>
            Identidad protegida
          </span>
        </div>
        <div className="text-[13px] mt-1" style={{ color: "#70667A" }}>
          {t.country} · {t.accent}
        </div>
      </div>
      <div className="text-right">
        <div className="font-mono-num text-[15px] font-medium" style={{ color: "#1E1924" }}>
          {t.rating.toFixed(2)} ★
        </div>
        <div className="text-[11px]" style={{ color: "#70667A" }}>
          {t.sessions_count.toLocaleString("es-ES")} clases
        </div>
      </div>
    </div>
    <p className="text-[14px] leading-[1.55] max-w-prose-tight" style={{ color: "#70667A" }}>
      {t.bio}
    </p>
    <div className="flex items-center justify-between pt-4 border-t" style={{ borderColor: "#DEDAE2" }}>
      <span className="wk-chip wk-chip-accent">{t.specialty}</span>
      <div className="text-right">
        <div className="font-mono-num text-[19px] font-bold tracking-hero" style={{ color: "#1E1924" }}>
          $22
        </div>
        <div className="text-[11px]" style={{ color: "#70667A" }}>
          Pack de prueba
        </div>
      </div>
    </div>
  </Link>
);

const SkelCard = () => (
  <div className="wk-card p-6 flex flex-col gap-4">
    <div className="flex items-center gap-4">
      <div className="wk-skel" style={{ width: 56, height: 56, borderRadius: 999 }} />
      <div className="flex-1">
        <div className="wk-skel" style={{ height: 16, width: "60%", marginBottom: 8 }} />
        <div className="wk-skel" style={{ height: 12, width: "40%" }} />
      </div>
    </div>
    <div className="wk-skel" style={{ height: 12, width: "95%" }} />
    <div className="wk-skel" style={{ height: 12, width: "80%" }} />
    <div className="wk-skel" style={{ height: 32, width: "100%" }} />
  </div>
);

export default function CatalogPage() {
  const [tutors, setTutors] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [accent, setAccent] = useState(null);
  const [specialty, setSpecialty] = useState(null);
  const [q, setQ] = useState("");
  const [sort, setSort] = useState("popular");

  const fetchData = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const params = { sort };
      if (accent) params.accent = accent;
      if (specialty) params.specialty = specialty;
      if (q) params.q = q;
      const data = await listTutors(params);
      setTutors(data);
    } catch (e) {
      setError("No se ha podido cargar el catálogo. Reintenta.");
    } finally {
      setLoading(false);
    }
  }, [accent, q, sort, specialty]);

  useEffect(() => {
    const timeoutId = window.setTimeout(fetchData, q ? 250 : 0);
    return () => window.clearTimeout(timeoutId);
  }, [fetchData, q]);

  return (
    <div className="max-w-[1280px] mx-auto px-6 lg:px-10 py-16 lg:py-20">
      <div className="flex items-end justify-between flex-wrap gap-6">
        <div>
          <div className="wk-eyebrow mb-4">Paso 2 · Elegir tutor</div>
          <h1 className="text-[42px] lg:text-[56px] font-extrabold tracking-hero leading-[1.05]" style={{ color: "#1E1924" }}>
            El catálogo real.<br />Sin apellidos.
          </h1>
          <p className="mt-5 text-[15px] max-w-prose-tight leading-[1.6]" style={{ color: "#70667A" }}>
            Todos los tutores están verificados a mano. Su identidad completa no se muestra: es la única manera de garantizar que WeSpik cubre la relación de principio a fin.
          </p>
        </div>
        <div className="flex items-center gap-3 w-full sm:w-auto">
          <input
            className="wk-input"
            style={{ maxWidth: 320 }}
            placeholder="Buscar por especialidad, nombre…"
            value={q}
            onChange={(e) => setQ(e.target.value)}
            data-testid="catalog-search-input"
          />
        </div>
      </div>

      {/* Filters */}
      <div className="mt-10 flex flex-col gap-4">
        <div className="flex flex-wrap items-center gap-2">
          <span className="text-[12px] uppercase tracking-[0.14em] mr-1" style={{ color: "#70667A" }}>Acento</span>
          {ACCENTS.map((a) => (
            <button
              key={String(a.id)}
              onClick={() => setAccent(a.id)}
              className={`wk-chip ${accent === a.id ? "wk-chip-active" : ""}`}
              data-testid={`filter-accent-${a.id || "all"}`}
            >
              {a.label}
            </button>
          ))}
        </div>
        <div className="flex flex-wrap items-center gap-2">
          <span className="text-[12px] uppercase tracking-[0.14em] mr-1" style={{ color: "#70667A" }}>Especialidad</span>
          <button onClick={() => setSpecialty(null)} className={`wk-chip ${!specialty ? "wk-chip-active" : ""}`} data-testid="filter-specialty-all">
            Todas
          </button>
          {SPECIALTIES.map((s) => (
            <button
              key={s}
              onClick={() => setSpecialty(s)}
              className={`wk-chip ${specialty === s ? "wk-chip-active" : ""}`}
              data-testid={`filter-specialty-${s}`}
            >
              {s}
            </button>
          ))}
        </div>
        <div className="flex flex-wrap items-center gap-2">
          <span className="text-[12px] uppercase tracking-[0.14em] mr-1" style={{ color: "#70667A" }}>Ordenar</span>
          {SORTS.map((s) => (
            <button
              key={s.id}
              onClick={() => setSort(s.id)}
              className={`wk-chip ${sort === s.id ? "wk-chip-active" : ""}`}
              data-testid={`sort-${s.id}`}
            >
              {s.label}
            </button>
          ))}
        </div>
      </div>

      {/* Grid */}
      <div className="mt-10 flex items-center justify-between">
        <div className="text-[14px]" style={{ color: "#70667A" }}>
          {loading ? "Cargando…" : `${tutors.length} tutor${tutors.length === 1 ? "" : "es"} disponible${tutors.length === 1 ? "" : "s"}`}
        </div>
      </div>

      {error && (
        <div className="mt-6 wk-card p-8 border-2" style={{ borderColor: "#7228C8" }}>
          <div className="text-[15px] font-semibold" style={{ color: "#1E1924" }}>Algo se ha atascado.</div>
          <p className="text-[13px] mt-1" style={{ color: "#70667A" }}>{error}</p>
          <button onClick={fetchData} className="wk-btn wk-btn-primary mt-4" data-testid="catalog-retry-btn">Reintentar</button>
        </div>
      )}

      {loading && !error && (
        <div className="mt-6 grid md:grid-cols-2 xl:grid-cols-3 gap-5">
          {[0, 1, 2, 3, 4, 5].map((i) => <SkelCard key={i} />)}
        </div>
      )}

      {!loading && !error && tutors.length === 0 && (
        <div className="mt-6 wk-card p-14 text-center">
          <div className="mx-auto w-16 h-16 rounded-2xl mb-6" style={{ background: "rgba(114,40,200,0.08)", border: "1px dashed #7228C8" }} />
          <h3 className="text-[22px] font-bold tracking-hero" style={{ color: "#1E1924" }}>
            No hay tutores con esos filtros.
          </h3>
          <p className="mt-3 text-[14px]" style={{ color: "#70667A" }}>
            Prueba a quitar alguno. O escríbenos y buscamos uno específico para ti.
          </p>
          <button
            onClick={() => { setAccent(null); setSpecialty(null); setQ(""); }}
            className="wk-btn wk-btn-primary mt-6"
            data-testid="catalog-clear-filters"
          >
            Limpiar filtros
          </button>
        </div>
      )}

      {!loading && !error && tutors.length > 0 && (
        <div className="mt-6 grid md:grid-cols-2 xl:grid-cols-3 gap-5" data-testid="tutor-list">
          {tutors.map((t) => <TutorCard key={t.id} t={t} />)}
        </div>
      )}
    </div>
  );
}
